import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { apiService } from '@/services/api';

interface GroupSummary {
  id: string; title: string; commodity: string | null; description: string | null;
  memberCount?: number; viewerRole: string | null; viewerStatus: string | null;
}

const ROLE_LABEL: Record<string, string> = { mediator: 'You mediate', seller: 'Seller', buyer: 'Buyer' };

const Marketplace: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'discover' | 'mine'>('discover');
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', commodity: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = tab === 'discover' ? await apiService.marketListGroups(search.trim() || undefined) : await apiService.marketMyGroups();
      setGroups(res?.data?.groups || []);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load groups'); }
    finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim() || creating) return;
    setCreating(true); setError(null);
    try {
      const res = await apiService.marketCreateGroup({ title: form.title.trim(), commodity: form.commodity.trim() || undefined, description: form.description.trim() || undefined });
      const id = res?.data?.group?.id;
      setShowCreate(false); setForm({ title: '', commodity: '', description: '' });
      if (id) navigate(`/customer/marketplace/${id}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not create the group'); }
    finally { setCreating(false); }
  };

  return (
    <div style={st.page}>
      <DashboardHeader />
      <div style={st.wrap}>
        <div style={st.head}>
          <h1 style={st.h1}>🛒 Marketplace</h1>
          <p style={st.sub}>Broker-run groups. A mediator connects buyers and sellers — identities stay private.</p>
        </div>
        <button style={st.createWide} onClick={() => setShowCreate(true)}>＋ Create a group</button>

        <div style={st.tabs}>
          <button style={tab === 'discover' ? st.tabOn : st.tab} onClick={() => setTab('discover')}>Discover</button>
          <button style={tab === 'mine' ? st.tabOn : st.tab} onClick={() => setTab('mine')}>My groups</button>
        </div>

        {tab === 'discover' && (
          <input style={st.search} placeholder="Search by title or commodity…" value={search} onChange={(e) => setSearch(e.target.value)} />
        )}

        {error && <div style={st.error}>{error}</div>}

        {loading ? (
          <div style={st.empty}>Loading…</div>
        ) : groups.length === 0 ? (
          <div style={st.emptyWrap}>
            <div style={{ fontSize: 34 }}>🛒</div>
            <div style={st.emptyText}>{tab === 'discover' ? 'No open groups yet.' : 'You haven’t joined any groups yet.'}</div>
            <button style={st.createWide} onClick={() => setShowCreate(true)}>＋ Create a group</button>
          </div>
        ) : (
          <div style={st.list}>
            {groups.map((g) => (
              <button key={g.id} style={st.card} onClick={() => navigate(`/customer/marketplace/${g.id}`)}>
                <div style={st.cardTop}>
                  <span style={st.cardTitle}>{g.title}</span>
                  {g.viewerRole && <span style={st.roleTag}>{g.viewerStatus === 'requested' ? 'Requested' : ROLE_LABEL[g.viewerRole] || g.viewerRole}</span>}
                </div>
                {g.commodity && <div style={st.commodity}>🏷️ {g.commodity}</div>}
                {g.description && <div style={st.desc}>{g.description}</div>}
                <div style={st.cardMeta}>{typeof g.memberCount === 'number' ? `${g.memberCount} member${g.memberCount === 1 ? '' : 's'}` : ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Always-reachable Create, even when the top scrolls under the fixed app header. */}
      <button style={st.fab} onClick={() => setShowCreate(true)} aria-label="Create a group">＋</button>

      {showCreate && (
        <div style={st.overlay} onClick={() => !creating && setShowCreate(false)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={st.modalH}>Create a group</h3>
            <p style={st.modalSub}>You’ll be the mediator. You approve who joins and only you see who’s who.</p>
            <label style={st.label}>Purpose / title</label>
            <input style={st.input} placeholder="e.g. Nice Potato" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <label style={st.label}>Commodity (optional)</label>
            <input style={st.input} placeholder="e.g. Potato" value={form.commodity} onChange={(e) => setForm({ ...form, commodity: e.target.value })} />
            <label style={st.label}>Description (optional)</label>
            <textarea style={{ ...st.input, minHeight: 70, resize: 'vertical' }} placeholder="What's this group for?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div style={st.modalActions}>
              <button style={st.ghost} onClick={() => setShowCreate(false)} disabled={creating}>Cancel</button>
              <button style={st.primary} onClick={() => void create()} disabled={!form.title.trim() || creating}>{creating ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const st: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#f6f7f9' },
  wrap: { maxWidth: 720, margin: '0 auto', padding: '84px 14px 96px' },
  head: { marginBottom: 12 },
  h1: { fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#0f172a' },
  sub: { margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' },
  createWide: { width: '100%', background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 14px', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', marginBottom: 14, boxShadow: '0 4px 12px var(--accent-ring, rgba(224,83,31,0.25))' },
  fab: { position: 'fixed', right: 18, bottom: 22, width: 56, height: 56, borderRadius: '50%', background: 'var(--accent-color)', color: '#fff', border: 'none', fontSize: 30, lineHeight: '52px', cursor: 'pointer', boxShadow: '0 8px 20px var(--accent-ring, rgba(224,83,31,0.25))', zIndex: 1000 },
  emptyWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '40px 16px' },
  emptyText: { color: '#94a3b8', fontSize: '0.95rem' },
  tabs: { display: 'flex', gap: 8, marginBottom: 12 },
  tab: { flex: 1, padding: '9px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, cursor: 'pointer' },
  tabOn: { flex: 1, padding: '9px 0', borderRadius: 9, border: '1px solid var(--accent-color)', background: 'var(--accent-soft, #fdeee7)', color: 'var(--accent-color)', fontWeight: 800, cursor: 'pointer' },
  search: { width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: '0.9rem', marginBottom: 12 },
  error: { background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem', marginBottom: 10 },
  empty: { textAlign: 'center', color: '#94a3b8', padding: '48px 12px' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { textAlign: 'left', background: '#fff', border: '1px solid #eef1f5', borderRadius: 14, padding: 14, cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardTitle: { fontWeight: 800, fontSize: '1.02rem', color: '#0f172a' },
  roleTag: { fontSize: '0.68rem', fontWeight: 800, color: 'var(--accent-color)', background: 'var(--accent-soft, #fdeee7)', borderRadius: 999, padding: '2px 9px' },
  commodity: { fontSize: '0.82rem', color: '#475569', marginTop: 4 },
  desc: { fontSize: '0.82rem', color: '#64748b', marginTop: 4 },
  cardMeta: { fontSize: '0.74rem', color: '#94a3b8', marginTop: 8 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 5000 },
  modal: { background: '#fff', borderRadius: 16, width: 'min(440px,96vw)', padding: 20 },
  modalH: { margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' },
  modalSub: { margin: '4px 0 14px', color: '#64748b', fontSize: '0.82rem' },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', margin: '10px 0 5px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #d1d5db', fontSize: '0.9rem', fontFamily: 'inherit' },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 },
  ghost: { padding: '9px 16px', borderRadius: 9, border: '1px solid #d1d5db', background: '#fff', fontWeight: 700, cursor: 'pointer' },
  primary: { padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--accent-color)', color: '#fff', fontWeight: 800, cursor: 'pointer' },
};

export default Marketplace;
